import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { message, Form } from 'antd';
import {
  useCreatePasswordMutation,
  useResetPasswordMutation,
} from '../../store/services/authService';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TitleText from '../atoms/TitleText';
import InfoText from '../atoms/InfoText';
import PasswordInput from '../atoms/PasswordInput';
import { AuthWrapper } from './AuthWrapper';
import Button from '../atoms/Button';

const resetPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters')
      .max(12, 'Password must be at most 12 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(
        /[^A-Za-z0-9]/,
        'Password must contain at least one special character'
      ),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export interface PasswordValidation {
  text: string;
  passed: boolean;
}

const validatePassword = (password: string): PasswordValidation[] => {
  return [
    {
      text: 'Average Length: The password must be at least 8-12 characters long.',
      passed: password.length >= 8 && password.length <= 12,
    },
    {
      text: 'Uppercase Letters: The password must contain at least one uppercase letter (A-Z).',
      passed: /[A-Z]/.test(password),
    },
    {
      text: 'Lower Case: The password must contain at least one lowercase letter (a-z).',
      passed: /[a-z]/.test(password),
    },
    {
      text: 'Numbers: The password must contain at least one digit (0-9).',
      passed: /[0-9]/.test(password),
    },
    {
      text: 'Special Characters: The password must contain at least one special character (e.g., !@#$%^&*).',
      passed: /[^A-Za-z0-9]/.test(password),
    },
  ];
};

export function SetupPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const type = searchParams.get('type');

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    mode: 'onChange',
  });

  const [resetPassword, { isLoading: isResettingPassword }] =
    useResetPasswordMutation();
  const [createPassword, { isLoading: isCreatingPassword }] =
    useCreatePasswordMutation();
  const navigate = useNavigate();

  const newPassword = watch('newPassword') || '';
  const confirmPassword = watch('confirmPassword') || '';

  const passwordValidations = useMemo(
    () => validatePassword(newPassword),
    [newPassword]
  );

  const confirmPasswordValidations = useMemo(() => {
    // if (!confirmPassword || !newPassword) return [];
    const match = newPassword === confirmPassword;
    return [
      {
        text: match
          ? 'Your password match, you are all set'
          : "Passwords don't match",
        passed: match,
      },
    ];
  }, [newPassword, confirmPassword]);

  useEffect(() => {
    if (!token) {
      message.error('Invalid or missing reset token');
      navigate('/login');
    }
  }, [token, navigate]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      message.error('Invalid reset token');
      return;
    }

    try {
      if (type === 'RESET') {
        await resetPassword({
          token,
          password: data.newPassword,
          confirmPassword: data.confirmPassword,
        }).unwrap();
        message.success('Password reset successful!');
      } else if (type === 'CREATE') {
        await createPassword({
          token,
          password: data.newPassword,
          confirmPassword: data.confirmPassword,
        }).unwrap();
        message.success('Password created successfully!');
      } else {
        message.error('Invalid reset type');
        return;
      }

      navigate('/login', { replace: true });
    } catch (error: any) {
      message.error(
        error?.data?.errors?.default ||
          Object.values(error?.data?.errors || {}).join(', ')
      );
    }
  };

  if (!token) {
    return null;
  }

  return (
    <AuthWrapper>
      <div className="w-full max-w-xl space-y-8" data-test="setup-password-page">
        <div className="">
          <TitleText
            text="Set Your New Password!"
            className="!text-2xl !font-bold mb-3"
          />
          <InfoText text="Please create a strong password to ensure your account's safety. Your security is important to us!" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-8" data-test="setup-password-form">
          <Form.Item
            label={
              <span className="text-gray-900 font-medium text-sm">
                Password
              </span>
            }
            validateStatus={errors.newPassword ? 'error' : ''}
            className="mb-6"
            layout="vertical"
          >
            <Controller
              name="newPassword"
              control={control}
              render={({ field }) => (
                <PasswordInput
                  {...field}
                  id="newPassword"
                  placeholder="***********"
                  autoComplete="new-password"
                  validationMessages={passwordValidations}
                  data-test="setup-password-new-input"
                />
              )}
            />
          </Form.Item>

          <Form.Item
            label={
              <span className="text-gray-900 font-medium text-sm">
                Confirm password
              </span>
            }
            validateStatus={errors.confirmPassword ? 'error' : ''}
            className="mb-6"
            layout="vertical"
          >
            <Controller
              name="confirmPassword"
              control={control}
              render={({ field }) => (
                <PasswordInput
                  {...field}
                  id="confirmPassword"
                  placeholder="***********"
                  autoComplete="new-password"
                  validationMessages={confirmPasswordValidations}
                  data-test="setup-password-confirm-input"
                />
              )}
            />
          </Form.Item>

          <Form.Item className="">
            <Button
              type="submit"
              variant="dark"
              disabled={
                isResettingPassword || isCreatingPassword || isSubmitting
              }
              className="w-full font-semibold h-12"
              data-test="setup-password-submit-btn"
            >
              Save
            </Button>
          </Form.Item>
        </form>
      </div>
    </AuthWrapper>
  );
}
